// ============================================================================
// AUDIT LOG SERVICE
// Service for fetching and managing audit logs
// ============================================================================

import { api } from './api';

export interface AuditEvent {
  eventId: string;
  eventType: string;
  timestamp: string;
  actorUserId: string;
  organizationId: string;
  action: string;
  status: 'success' | 'failure' | 'pending';
  severity: 'info' | 'warning' | 'error' | 'critical';
  eventHash: string;
  previousEventHash?: string;
  resourceType?: string;
  resourceId?: string;
  resourceName?: string;
  metadata?: Record<string, any>;
  errorMessage?: string;
  errorCode?: string;
  containsPII?: boolean;
  gdprRelevant?: boolean;
  actorIpAddress?: string;
  actorUserAgent?: string;
}

export interface AuditEventStatistics {
  totalEvents: number;
  eventsByType: Record<string, number>;
  eventsBySeverity: Record<string, number>;
  eventsByStatus: Record<string, number>;
  timeRange?: {
    earliest: string;
    latest: string;
  };
}

export interface HashChainVerificationResult {
  isValid: boolean;
  totalEventsChecked: number;
  brokenAtEventId?: string;
  brokenAtTimestamp?: string;
  expectedHash?: string;
  actualHash?: string;
  message?: string;
}

export interface EventTypeCategories {
  total: number;
  categories: {
    auth: string[];
    user: string[];
    session: string[];
    workflow: string[];
    data: string[];
    admin: string[];
    gdpr: string[];
    security: string[];
    template: string[];
    org: string[];
    project: string[];
  };
  allTypes: string[];
}

/**
 * Fetch audit events with optional filters
 */
export async function listAuditEvents(params?: {
  organizationId?: string;
  userId?: string;
  eventType?: string;
  severity?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  nextToken?: string;
  allOrganizations?: boolean;
}): Promise<{ events: AuditEvent[]; total: number; nextToken?: string }> {
  const queryParams = new URLSearchParams();

  if (params?.limit) queryParams.append('limit', String(params.limit));
  if (params?.nextToken) queryParams.append('nextToken', params.nextToken);
  if (params?.allOrganizations) queryParams.append('allOrganizations', 'true');
  if (params?.startDate) queryParams.append('startDate', params.startDate);
  if (params?.endDate) queryParams.append('endDate', params.endDate);
  if (params?.eventType) queryParams.append('eventType', params.eventType);
  if (params?.severity) queryParams.append('severity', params.severity);
  if (params?.status) queryParams.append('status', params.status);

  const response = await api.get(`/api/compliance/audit/events?${queryParams.toString()}`);
  return response.data;
}

/**
 * Get audit statistics for current organization
 */
export async function getAuditStatistics(
  allOrganizations?: boolean
): Promise<AuditEventStatistics> {
  const queryParams = new URLSearchParams();
  if (allOrganizations) queryParams.append('allOrganizations', 'true');

  const response = await api.get(`/api/compliance/audit/statistics?${queryParams.toString()}`);
  return response.data;
}

/**
 * Get all available event types grouped by category
 */
export async function getEventTypes(): Promise<EventTypeCategories> {
  const response = await api.get('/api/compliance/audit/event-types');
  return response.data;
}

/**
 * Verify hash chain integrity
 */
export async function verifyHashChain(
  limit?: number
): Promise<HashChainVerificationResult> {
  const response = await api.post('/api/compliance/audit/verify-chain', {
    limit: limit || 100
  });
  return response.data;
}

/**
 * Rebuild hash chain for current organization (ADMIN ONLY)
 * WARNING: This recalculates all event hashes - use only when chain is broken
 */
export async function rebuildHashChain(): Promise<{
  message: string;
  success: boolean;
  organizationId: string;
  eventsProcessed: number;
  eventsUpdated: number;
  errors: string[];
  newChainValid: boolean;
}> {
  const response = await api.post('/api/compliance/audit/rebuild-chain');
  return response.data;
}

/**
 * Export audit events to CSV format
 */
export function exportAuditEventsToCSV(events: AuditEvent[]): string {
  const headers = [
    'Event ID',
    'Event Type',
    'Timestamp',
    'Actor User ID',
    'Organization ID',
    'Action',
    'Status',
    'Severity',
    'Resource Type',
    'Resource ID',
    'IP Address',
    'Error Message'
  ];

  const rows = events.map(event => [
    event.eventId,
    event.eventType,
    event.timestamp,
    event.actorUserId,
    event.organizationId,
    event.action,
    event.status,
    event.severity,
    event.resourceType || '',
    event.resourceId || '',
    event.actorIpAddress || '',
    event.errorMessage || ''
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
  ].join('\n');

  return csvContent;
}

/**
 * Download CSV file
 */
export function downloadCSV(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Export audit events to JSON format
 */
export function exportAuditEventsToJSON(events: AuditEvent[]): string {
  return JSON.stringify(events, null, 2);
}

/**
 * Download JSON file
 */
export function downloadJSON(jsonContent: string, filename: string): void {
  const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
